import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp } from '../../icons';
import { getDateLocale } from '../../lib/base-i18n';
import { tCalendar } from '../../lib/calendar-i18n';

interface MonthYearPickerProps {
  /** 헤더가 지금 보여주는 연/월(선택 표시 기준) — month 는 0-11. */
  year: number;
  month: number;
  onPick: (year: number, month: number) => void;
}

const YEARS_PER_PAGE = 12;

/**
 * 헤더 타이틀 팝오버 — 연도/월 드릴다운 피커.
 *
 * 연도부터 고른다 — "년" 단계(기본, 12년 블록 그리드)에서 하나를 고르면 그 해의
 * "월" 단계(1~12월 12칸 그리드)로 내려간다. 월 단계에서 달을 고르면 그제서야 onPick 이
 * 불린다. 월 단계의 "YYYY년 ▲" 를 누르면 다시 년 단계로 돌아갈 수 있다.
 * 팝오버가 열릴 때마다(조건부 렌더 → 매번 새 마운트) 년 단계로 초기화된다.
 */
export function MonthYearPicker({ year, month, onPick }: MonthYearPickerProps) {
  const [level, setLevel] = useState<'month' | 'year'>('year');
  const [viewYear, setViewYear] = useState(year);
  const [yearBlockStart, setYearBlockStart] = useState(() => Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE);

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  function openYearLevel() {
    setYearBlockStart(Math.floor(viewYear / YEARS_PER_PAGE) * YEARS_PER_PAGE);
    setLevel('year');
  }
  function pickYear(y: number) {
    setViewYear(y);
    setLevel('month');
  }

  return (
    <div className="cal-myp">
      {level === 'month' ? (
        <>
          <div className="cal-myp-head">
            <button
              type="button"
              className="cal-myp-navbtn"
              aria-label={tCalendar('calendar.nav.prev_year')}
              onClick={() => setViewYear((y) => y - 1)}
            >
              <ChevronLeft size={15} />
            </button>
            <button type="button" className="cal-myp-title" onClick={openYearLevel}>
              {tCalendar('calendar.picker.year_label', { year: viewYear })}
              <ChevronUp size={13} aria-hidden />
            </button>
            <button
              type="button"
              className="cal-myp-navbtn"
              aria-label={tCalendar('calendar.nav.next_year')}
              onClick={() => setViewYear((y) => y + 1)}
            >
              <ChevronRight size={15} />
            </button>
          </div>
          <div className="cal-myp-grid">
            {Array.from({ length: 12 }, (_, m) => {
              const isSel = viewYear === year && m === month;
              const isToday = viewYear === todayYear && m === todayMonth;
              return (
                <button
                  key={m}
                  type="button"
                  className={`cal-myp-cell${isSel ? ' is-sel' : ''}${isToday ? ' is-today' : ''}`}
                  onClick={() => onPick(viewYear, m)}
                >
                  {monthLabel(viewYear, m)}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="cal-myp-head">
            <button
              type="button"
              className="cal-myp-navbtn"
              aria-label={tCalendar('calendar.nav.prev_year_block')}
              onClick={() => setYearBlockStart((s) => s - YEARS_PER_PAGE)}
            >
              <ChevronLeft size={15} />
            </button>
            <span className="cal-myp-title cal-myp-title--static">
              {tCalendar('calendar.picker.year_range', { from: yearBlockStart, to: yearBlockStart + YEARS_PER_PAGE - 1 })}
            </span>
            <button
              type="button"
              className="cal-myp-navbtn"
              aria-label={tCalendar('calendar.nav.next_year_block')}
              onClick={() => setYearBlockStart((s) => s + YEARS_PER_PAGE)}
            >
              <ChevronRight size={15} />
            </button>
          </div>
          <div className="cal-myp-grid">
            {Array.from({ length: YEARS_PER_PAGE }, (_, i) => {
              const y = yearBlockStart + i;
              const isSel = y === year;
              const isToday = y === todayYear;
              return (
                <button
                  key={y}
                  type="button"
                  className={`cal-myp-cell${isSel ? ' is-sel' : ''}${isToday ? ' is-today' : ''}`}
                  onClick={() => pickYear(y)}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function monthLabel(y: number, m: number): string {
  return new Date(y, m, 1).toLocaleDateString(getDateLocale(), { month: 'short' });
}
